import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useAuditoria, useConfiguracio, useDesaConfiguracio } from '@/api/administracio';
import { Desplegable } from '@/components/Desplegable';
import { Icona } from '@/components/Icona';
import { useFormat } from '@/i18n/useFormat';
import { Toolbar } from '@/layout/Toolbar';
import { ErrorCarrega } from '@/pages/EstatsPagina';
import { Buit, Camp } from '@/pages/operacio/comu';
import { useMe } from '@/permisos/MeContext';
import { textAccioAuditoria } from './auditoria';
import { CanvisAuditoria } from './CanvisAuditoria';
import { BarraCanvis } from './marc/canvis';
import { CapPanell, ErrorDesa } from './comu';
import './administracio.css';
import './sistema.css';

const FUSOS = ['Europe/Madrid', 'Atlantic/Canary', 'UTC'];
type Pestanya = 'ajustos' | 'historial';
const PESTANYES: Pestanya[] = ['ajustos', 'historial'];

function Historial() {
  const { t } = useTranslation('administracio');
  const f = useFormat();
  const [pagina, setPagina] = useState(0);
  const auditoria = useAuditoria({ entitat: 'configuracio', page: pagina });
  const items = auditoria.data?.items ?? [];
  const totalPagines = Math.max(1, Math.ceil((auditoria.data?.total ?? 0) / 20));
  return (
    <div className="panel">
      <CapPanell titol={t('configuracio.auditoria')} icona="clock-arrow" />
      {items.length === 0 ? (
        <Buit icona="clipboard">
          {auditoria.isLoading ? t('comu.carregant') : t('configuracio.senseCanvis')}
        </Buit>
      ) : (
        <>
          <div className="sis-taula-scroll">
            <table className="taula">
              <thead>
                <tr>
                  <th>{t('configuracio.quan')}</th>
                  <th>{t('configuracio.qui')}</th>
                  <th>{t('configuracio.canvi')}</th>
                  <th>{t('configuracio.resultat')}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((a) => (
                  <tr key={a.id}>
                    <td className="mono t-caption nowrap">{f.dataHora(a.at)}</td>
                    <td>{a.upn ?? '—'}</td>
                    <td>
                      {textAccioAuditoria(a.accio, t)}
                      {a.entitatId ? <span className="codi">{` · ${a.entitatId}`}</span> : null}
                      {a.resultat === 'OK' ? (
                        <CanvisAuditoria valorPrevi={a.valorPrevi} valorNou={a.valorNou} />
                      ) : null}
                    </td>
                    <td>
                      <span
                        className={`badge ${a.resultat === 'OK' ? 'badge--ok' : 'badge--critic'}`}
                      >
                        {a.resultat === 'OK'
                          ? t('configuracio.resultatOk')
                          : t('configuracio.resultatKo')}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="panel__foot">
            <div className="spacer" />
            <button
              type="button"
              className="btn btn--xs"
              disabled={pagina === 0}
              onClick={() => setPagina(pagina - 1)}
            >
              {t('comu.anterior')}
            </button>
            <span className="t-caption dim">
              {t('comu.pagina', { actual: pagina + 1, total: totalPagines })}
            </span>
            <button
              type="button"
              className="btn btn--xs"
              disabled={pagina + 1 >= totalPagines}
              onClick={() => setPagina(pagina + 1)}
            >
              {t('comu.seguent')}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export function W31Configuracio() {
  const { t } = useTranslation('administracio');
  const tc = useTranslation('comu').t;
  const { pot } = useMe();
  const config = useConfiguracio();
  const desa = useDesaConfiguracio();
  const [pestanya, setPestanya] = useState<Pestanya>('ajustos');
  const [edicio, setV] = useState<Record<string, string> | null>(null);
  const editable = pot('cai.configuracio', 'MODIFICAR');
  const desat = config.data?.data.valors ?? {};
  const v = edicio ?? desat;

  const val = (k: string, d = '') => v[k] ?? d;
  const set = (k: string, x: string) => setV({ ...v, [k]: x });
  const anys = Number(val('retencio.anys', '3'));
  const retencioValida = Number.isFinite(anys) && anys >= 3;
  const minutsEscalat = Math.round(Number(val('escalat.temps.defecte.seg', '300')) / 60);
  const claus = new Set([...Object.keys(v), ...Object.keys(desat)]);
  const n = edicio ? [...claus].filter((k) => (v[k] ?? '') !== (desat[k] ?? '')).length : 0;

  return (
    <section className="pantalla" data-screen="W-31">
      <Toolbar titol={t('titols.configuracio')} />
      <div className="fitxa-pestanyes" role="tablist" aria-label={t('titols.configuracio')}>
        {PESTANYES.map((p) => (
          <button
            key={p}
            type="button"
            role="tab"
            aria-selected={pestanya === p}
            onClick={() => setPestanya(p)}
          >
            {t(`sistema.configuracio.pestanyes.${p}`)}
          </button>
        ))}
      </div>
      {config.isError ? (
        <ErrorCarrega onReintenta={() => void config.refetch()} />
      ) : pestanya === 'historial' ? (
        <div className="content stack">
          <Historial />
        </div>
      ) : (
        <div className="content stack sis-estret">
          <ErrorDesa
            error={desa.error}
            onRecarrega={() => {
              setV(null);
              desa.reset();
              void config.refetch();
            }}
          />
          {desa.isSuccess && n === 0 ? (
            <p className="info-banda" role="status">
              <Icona nom="checkmark-circle" />
              {t('comu.desat')}
            </p>
          ) : null}
          <div className="panel">
            <CapPanell
              titol={t('sistema.configuracio.regional')}
              icona="globe"
              colorIcona="c-blue"
            />
            <div className="panel__body grid grid-2" style={{ gap: 16 }}>
              <Camp etiqueta={t('configuracio.idioma')}>
                <Desplegable
                  aria-label={t('configuracio.idioma')}
                  valor={val('idioma.defecte', 'ca')}
                  disabled={!editable}
                  opcions={[
                    { valor: 'ca', etiqueta: tc('idioma.ca') },
                    { valor: 'es', etiqueta: tc('idioma.es') },
                  ]}
                  onCanvia={(x) => set('idioma.defecte', x)}
                />
              </Camp>
              <Camp etiqueta={t('configuracio.fus')}>
                <Desplegable
                  aria-label={t('configuracio.fus')}
                  valor={val('fus.horari', 'Europe/Madrid')}
                  disabled={!editable}
                  opcions={FUSOS.map((z) => ({
                    valor: z,
                    etiqueta: t(`sistema.configuracio.fusos.${z.replace('/', '_')}`, {
                      defaultValue: z,
                    }),
                    detall: z,
                  }))}
                  onCanvia={(x) => set('fus.horari', x)}
                />
              </Camp>
            </div>
          </div>
          <div className="panel">
            <CapPanell
              titol={t('sistema.configuracio.escalat')}
              icona="arrow-up"
              colorIcona="c-orange"
            />
            <div className="panel__body form">
              <Camp etiqueta={t('configuracio.escalatDefecte')}>
                <input
                  className="input sis-input-curt"
                  type="number"
                  min={1}
                  value={minutsEscalat}
                  disabled={!editable}
                  onChange={(e) =>
                    set(
                      'escalat.temps.defecte.seg',
                      String(Math.max(1, Number(e.target.value) || 1) * 60),
                    )
                  }
                />
              </Camp>
              <p className="camp__ajuda" style={{ margin: 0 }}>
                {t('sistema.configuracio.ajudaEscalat')}{' '}
                <Link to="/admin/circuits" className="sis-enllac">
                  {t('nav.circuits')}
                </Link>
                {' · '}
                <Link to="/admin/canals" className="sis-enllac">
                  {t('nav.canals')}
                </Link>
              </p>
            </div>
          </div>
          <div className="panel" data-rnf="RNF-002">
            <CapPanell
              titol={t('configuracio.procediment')}
              icona="doc-text"
              colorIcona="c-orange"
            />
            <div className="panel__body form">
              <p className="camp__ajuda" style={{ margin: 0 }}>
                {t('configuracio.ajudaProcediment')}
              </p>
              <Camp etiqueta={t('configuracio.telefon')}>
                <input
                  className="input sis-input-mig"
                  type="tel"
                  value={val('app.telefon.emergencia')}
                  disabled={!editable}
                  onChange={(e) => set('app.telefon.emergencia', e.target.value)}
                />
              </Camp>
              <div className="grid grid-2" style={{ gap: 16 }}>
                <Camp etiqueta={t('configuracio.procedimentCa')}>
                  <textarea
                    className="input"
                    rows={4}
                    value={val('procediment.alternatiu.ca')}
                    disabled={!editable}
                    onChange={(e) => set('procediment.alternatiu.ca', e.target.value)}
                  />
                </Camp>
                <Camp etiqueta={t('configuracio.procedimentEs')}>
                  <textarea
                    className="input"
                    rows={4}
                    value={val('procediment.alternatiu.es')}
                    disabled={!editable}
                    onChange={(e) => set('procediment.alternatiu.es', e.target.value)}
                  />
                </Camp>
              </div>
            </div>
          </div>
          <div className="panel">
            <CapPanell titol={t('configuracio.retencio')} icona="archive" colorIcona="c-purple" />
            <div className="panel__body form">
              <Camp etiqueta={t('configuracio.anys')}>
                <input
                  className="input sis-input-curt"
                  type="number"
                  min={3}
                  value={val('retencio.anys', '3')}
                  disabled={!editable}
                  aria-invalid={!retencioValida}
                  onChange={(e) => set('retencio.anys', e.target.value)}
                />
              </Camp>
              {!retencioValida ? (
                <p className="error-text">{t('configuracio.retencioMinima')}</p>
              ) : null}
              <p className="camp__ajuda" style={{ margin: 0 }}>
                {t('configuracio.ajudaRetencio')}
              </p>
            </div>
          </div>
        </div>
      )}
      {editable ? (
        <BarraCanvis
          n={n}
          nom={t('titols.configuracio')}
          desant={desa.isPending}
          desactivat={!retencioValida || !config.data}
          onDescarta={() => {
            setV(null);
            desa.reset();
          }}
          onDesa={() =>
            desa.mutate(
              { config: { valors: v }, etag: config.data?.etag ?? null },
              { onSuccess: () => setV(null) },
            )
          }
        >
          {!retencioValida ? t('sistema.comu.revisaErrors') : null}
        </BarraCanvis>
      ) : null}
    </section>
  );
}
